import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { Fragment, createElement, isValidElement, type ComponentType, type ReactNode } from 'react';

import { Menu, type MenuItem } from '@/components/ui/Menu';
import { cn } from '@/lib/cn';

/**
 * Breadcrumb — where you are, one level per crumb.
 *
 * Collapses the middle when it runs past `maxItems`: the first crumb, an overflow menu
 * holding the hidden ones, then the tail. The last crumb is the current page — it is
 * text, never a link, and carries `aria-current="page"`.
 */

export interface BreadcrumbItem {
  id?: string;
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: ComponentType<{ className?: string }> | ReactNode;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  /** Total crumbs shown before the middle collapses. Minimum 3. */
  maxItems?: number;
  className?: string;
  label?: string;
}

type CrumbNode =
  | { kind: 'crumb'; key: string; item: BreadcrumbItem; current: boolean }
  | { kind: 'overflow'; key: string; hidden: BreadcrumbItem[] };

export function Breadcrumb({
  items,
  maxItems = 4,
  className,
  label = 'Breadcrumb',
}: BreadcrumbProps) {
  if (items.length === 0) return null;

  const limit = Math.max(3, maxItems);
  const collapsed = items.length > limit;
  const nodes: CrumbNode[] = [];

  if (!collapsed) {
    items.forEach((item, index) => {
      nodes.push({
        kind: 'crumb',
        key: item.id ?? `c${index}`,
        item,
        current: index === items.length - 1,
      });
    });
  } else {
    const tailCount = limit - 2;
    const tail = items.slice(items.length - tailCount);
    const hidden = items.slice(1, items.length - tailCount);
    nodes.push({ kind: 'crumb', key: items[0].id ?? 'c0', item: items[0], current: false });
    nodes.push({ kind: 'overflow', key: 'overflow', hidden });
    tail.forEach((item, index) => {
      nodes.push({
        kind: 'crumb',
        key: item.id ?? `t${index}`,
        item,
        current: index === tail.length - 1,
      });
    });
  }

  return (
    <nav aria-label={label} className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-1.5">
        {nodes.map((node, index) => (
          <Fragment key={node.key}>
            {index > 0 ? (
              <li aria-hidden className="flex shrink-0 items-center">
                <ChevronRight className="size-3.5 text-muted" strokeWidth={2} />
              </li>
            ) : null}
            {node.kind === 'crumb' ? (
              <Crumb item={node.item} current={node.current} />
            ) : (
              <Overflow hidden={node.hidden} />
            )}
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}

function Overflow({ hidden }: { hidden: BreadcrumbItem[] }) {
  const overflowItems: MenuItem[] = hidden.map((item, index) => ({
    id: item.id ?? `crumb-${index}`,
    label: item.label,
    icon: item.icon,
    onSelect: () => {
      if (item.onClick) item.onClick();
      else if (item.href) window.location.assign(item.href);
    },
  }));

  return (
    <li className="flex shrink-0 items-center">
      <Menu
        items={overflowItems}
        label="Hidden breadcrumb levels"
        trigger={
          <button
            type="button"
            aria-label={`Show ${hidden.length} hidden levels`}
            className={cn(
              'flex h-[var(--h-control-sm)] items-center rounded-[var(--radius-sm)] px-1',
              'text-muted transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
              'hover:bg-[var(--surface-hover)] hover:text-primary',
              'active:bg-[var(--surface-active)]',
              'data-[state=open]:bg-[var(--surface-hover)] data-[state=open]:text-primary',
              'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
            )}
          >
            <MoreHorizontal className="size-4" aria-hidden />
          </button>
        }
      />
    </li>
  );
}

function Crumb({ item, current }: { item: BreadcrumbItem; current: boolean }) {
  const content = (
    <>
      <CrumbIcon icon={item.icon} />
      <span className="truncate">{item.label}</span>
    </>
  );

  if (current) {
    return (
      <li className="flex min-w-0 items-center">
        <span
          aria-current="page"
          className="flex min-w-0 items-center gap-1.5 px-1 text-base font-medium text-primary"
        >
          {content}
        </span>
      </li>
    );
  }

  const interactive = cn(
    'flex h-[var(--h-control-sm)] min-w-0 items-center gap-1.5 rounded-[var(--radius-sm)] px-1',
    'text-base text-muted transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
    'hover:bg-[var(--surface-hover)] hover:text-primary',
    'active:bg-[var(--surface-active)]',
    'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
  );

  return (
    <li className="flex min-w-0 items-center">
      {item.href ? (
        <a href={item.href} onClick={item.onClick} className={interactive}>
          {content}
        </a>
      ) : (
        <button type="button" onClick={item.onClick} className={interactive}>
          {content}
        </button>
      )}
    </li>
  );
}

function CrumbIcon({ icon }: { icon?: ComponentType<{ className?: string }> | ReactNode }) {
  if (!icon) return null;
  if (typeof icon === 'function') return createElement(icon, { className: 'size-3.5 shrink-0' });
  if (isValidElement(icon)) {
    return <span className="flex size-3.5 shrink-0 items-center">{icon}</span>;
  }
  return null;
}
